import React, { useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Loader2, RefreshCw, Check, AlertCircle, Wand2, Camera, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

// Map the AI's suggested service label back to the wizard's need key.
const SERVICE_TO_NEEDKEY = {
  "General Cleaning": "general",
  "General Deep Clean": "deep",
  "Move-In / Move-Out": "move",
  "Post-Construction": "post",
  "Property Detailing": "detail",
};

const Confidence = ({ value }) => {
  const pct = Math.round((value || 0) * 100);
  const tone = pct >= 66 ? "text-gold-soft" : pct >= 40 ? "text-secondary" : "text-muted-c";
  return (
    <div className="flex items-center gap-2" title={`Model confidence ${pct}%`} aria-label={`Confidence ${pct} percent`}>
      <div className="h-1.5 w-14 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-[color:var(--gold)]" style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-[11px] tabular-nums ${tone}`}>{pct}%</span>
    </div>
  );
};

const FindingSkeleton = () => (
  <div className="animate-pulse rounded-xl border border-[color:var(--border-subtle)] bg-black/30 p-4">
    <div className="h-3 w-24 rounded bg-white/10" />
    <div className="mt-3 h-2 w-full rounded bg-white/[0.07]" />
    <div className="mt-2 h-2 w-2/3 rounded bg-white/[0.07]" />
  </div>
);

/**
 * Flagship AI vision panel. Analyzes uploaded property photos and lets the
 * customer apply the aggregate suggestions to their plan selections.
 * Purely additive — the estimator engine and manual inputs remain the source of truth.
 */
export function AiPhotoAnalysisPanel({ media = [], context = {}, onApply, cardCls, kicker }) {
  const [status, setStatus] = useState("idle"); // idle | analyzing | results | error
  const [analysis, setAnalysis] = useState(null);
  const [applied, setApplied] = useState(false);

  const imageMedia = media.filter((m) => (m.content_type || "").startsWith("image"));
  const imagePaths = imageMedia.map((m) => m.path);
  const canAnalyze = imagePaths.length > 0;

  const run = async () => {
    if (!canAnalyze) { toast.error("Add at least one photo above to analyze."); return; }
    setStatus("analyzing"); setApplied(false);
    try {
      const { data } = await api.post("/property-intelligence/analyze", { image_paths: imagePaths, context });
      setAnalysis(data); setStatus("results");
    } catch (e) {
      console.error("PI photo analysis failed:", e);
      setStatus("error");
    }
  };

  const apply = () => {
    const agg = analysis?.aggregate;
    if (!agg || !onApply) return;
    const patch = {};
    if (agg.overall_condition) patch.conditionKey = agg.overall_condition;
    if (SERVICE_TO_NEEDKEY[agg.suggested_service]) patch.needKey = SERVICE_TO_NEEDKEY[agg.suggested_service];
    if (Number(agg.size_hint_sqm) > 0) patch.sqm = Number(agg.size_hint_sqm);
    onApply(patch, agg);
    setApplied(true);
    toast.success("AI suggestions applied to your plan");
  };

  const agg = analysis?.aggregate;

  return (
    <div className={cardCls} data-testid="pi-ai-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={kicker}>Ascendia Intelligence · AI vision <span className="text-muted-c normal-case tracking-normal">(optional)</span></p>
          <p className="mt-1 text-sm text-secondary">Let our AI read your photos and suggest the right service, condition and size — then apply it to your plan in one tap.</p>
        </div>
        <span className="hidden sm:flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-[color:var(--border-strong)] bg-gold/10"><Wand2 className="h-5 w-5 text-gold-soft" /></span>
      </div>

      {status !== "results" && (
        <button
          onClick={run}
          disabled={!canAnalyze || status === "analyzing"}
          data-testid="pi-ai-analyze-button"
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.99] transition-transform duration-150"
        >
          {status === "analyzing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {status === "analyzing" ? "Analyzing your photos…" : `Analyze ${imagePaths.length || ""} photo${imagePaths.length === 1 ? "" : "s"} with AI`}
        </button>
      )}
      {!canAnalyze && status === "idle" && (
        <p className="mt-2 text-[11px] text-muted-c flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> Add at least one photo above to enable AI analysis.</p>
      )}

      {status === "analyzing" && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="pi-ai-analyzing">
          <FindingSkeleton /><FindingSkeleton />
        </div>
      )}

      {status === "error" && (
        <div className="mt-4 rounded-xl border border-[color:var(--border-strong)] bg-black/30 p-4" data-testid="pi-ai-error">
          <p className="text-sm text-white flex items-center gap-2"><AlertCircle className="h-4 w-4 text-gold-soft" /> We couldn't analyze the photos just now.</p>
          <p className="mt-1 text-xs text-muted-c">Your plan still works perfectly — every field is editable manually.</p>
          <button onClick={run} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[color:var(--border-subtle)] px-4 py-2 text-xs text-white hover:bg-white/5"><RefreshCw className="h-3.5 w-3.5" /> Try again</button>
        </div>
      )}

      {status === "results" && agg && (
        <div className="mt-4 space-y-4" data-testid="pi-ai-results">
          {/* Aggregate recommendation */}
          <div className="rounded-xl border border-[color:var(--border-strong)] bg-gold/[0.05] p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-white flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold-soft" /> AI recommendation</p>
              <Confidence value={agg.confidence} />
            </div>
            <p className="mt-2 text-sm text-secondary">{agg.summary}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {agg.suggested_service && <span className="inline-flex items-center rounded-full border border-[color:var(--border-strong)] px-3 py-1 text-xs text-gold-soft">{agg.suggested_service}</span>}
              {agg.overall_condition && <span className="inline-flex items-center rounded-full border border-[color:var(--border-subtle)] px-3 py-1 text-xs text-secondary">Condition: {agg.overall_condition}</span>}
              {Number(agg.size_hint_sqm) > 0 && <span className="inline-flex items-center rounded-full border border-[color:var(--border-subtle)] px-3 py-1 text-xs text-secondary">~{agg.size_hint_sqm} m²</span>}
              {agg.construction_residue && <span className="inline-flex items-center rounded-full border border-[color:var(--border-subtle)] px-3 py-1 text-xs text-secondary">Construction residue detected</span>}
            </div>
            {agg.recommended_addons?.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-c">Suggested extras</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {agg.recommended_addons.map((a, i) => <span key={`${a}-${i}`} className="inline-flex items-center rounded-full border border-[color:var(--border-subtle)] px-2.5 py-0.5 text-[11px] text-secondary">{a}</span>)}
                </div>
              </div>
            )}
            {agg.cautions?.length > 0 && (
              <div className="mt-3 rounded-lg border border-[color:var(--border-subtle)] bg-black/30 p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-c flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5 text-gold-soft" /> Honest cautions</p>
                <ul className="mt-1.5 space-y-1">
                  {agg.cautions.map((c, i) => <li key={i} className="text-xs text-muted-c">• {c}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button onClick={apply} disabled={applied} data-testid="pi-ai-apply-button" className="inline-flex items-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50 active:scale-[0.99] transition-transform duration-150">
                {applied ? <Check className="h-4 w-4" /> : <Wand2 className="h-4 w-4" />} {applied ? "Applied" : "Apply AI suggestions"}
              </button>
              <button onClick={run} className="inline-flex items-center gap-1.5 rounded-xl border border-[color:var(--border-subtle)] px-4 py-2.5 text-xs text-white hover:bg-white/5"><RefreshCw className="h-3.5 w-3.5" /> Re-analyze</button>
            </div>
            <p className="mt-2 text-[11px] text-muted-c">AI reads photos, not surfaces — final scope and price are always confirmed by Ascendia after review.</p>
          </div>

          {/* Per-photo findings */}
          {analysis.photos?.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {analysis.photos.map((p, i) => (
                <div key={i} data-testid="pi-ai-photo-finding" className="rounded-xl border border-[color:var(--border-subtle)] bg-black/30 overflow-hidden">
                  <div className="flex">
                    {imageMedia[p.index ?? i]?.preview && (
                      <img src={imageMedia[p.index ?? i].preview} alt="" className="h-24 w-24 flex-shrink-0 object-cover" />
                    )}
                    <div className="p-3 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-white truncate">{p.room}</p>
                        <Confidence value={p.confidence} />
                      </div>
                      <p className="mt-0.5 text-[11px] text-gold-soft">{p.condition} · {p.diy_or_pro} · {p.size_hint}</p>
                      <p className="mt-1 text-xs text-muted-c line-clamp-2">{p.note || p.surface_note}</p>
                    </div>
                  </div>
                  {p.likely_issues?.length > 0 && (
                    <div className="px-3 pb-3 flex flex-wrap gap-1.5">
                      {p.likely_issues.map((it, k) => <span key={k} className="inline-flex items-center rounded-full border border-[color:var(--border-subtle)] px-2 py-0.5 text-[10px] text-secondary">{it}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AiPhotoAnalysisPanel;
